/**
 * viewportGroup90ClipBit — export guard for the AcDbViewport group-90
 * non-rectangular-clip bit (0x10000) — @codex #496 P1.
 *
 * The 0x10000 bit is only a VALID export when PAIRED with a real 340 clip-
 * boundary handle (Autodesk DXF reference AcDbViewport / ezdxf acdb_viewport:
 * the flag without a boundary handle is an invalid viewport). An in-session
 * VPCLIP-authored polygon clip (`applyViewportClipSetPolygon`) sets the 0x10000
 * bit + a typed `clipBoundaryPaperSpace`, but SYNTHESISING the SPATIAL_FILTER +
 * 340 on write is a deferred scope — until then there is no 340 to pair with, so
 * emitting the lone bit would write a CORRUPT viewport (non-rect flag, no
 * boundary). `maskOrphanNonRectClipBit` clears the bit whenever no real clip
 * boundary is emitted.
 *
 * A boundary counts as emitted when EITHER:
 *  - a real native 340 clip-boundary handle is written (the typed
 *    `clippingBoundaryHandle` OR a legacy extraGroups-340, each ≠ the "0" no-clip
 *    sentinel), OR
 *  - the viewport carries a PRESERVED imported SPATIAL_FILTER (XCLIP/VPCLIP) clip
 *    whose boundary rides the extension-dictionary ACAD_FILTER → SPATIAL_FILTER
 *    chain (round-tripped verbatim through the OBJECTS passthrough) rather than a
 *    native 340 handle. That carrier is signalled by `clipBoundaryKind`, which is
 *    set ONLY when the import resolver (`viewportClipFinalize` ← dxf-io
 *    `resolveViewportClip`) materialised a SPATIAL_FILTER boundary and cleared by
 *    `invalidateViewportClip` when the clip is removed/superseded — so its
 *    presence means a valid source clip is still exported and the source non-rect
 *    bit must be kept, not masked (@codex #496 P2).
 *
 * This keeps an IMPORTED clipped viewport's flag verbatim (native-340 OR
 * SPATIAL_FILTER) while never exporting an orphan authored bit.
 */

import type { ViewportEntity } from "./model";

/**
 * DXF group-90 AcDbViewport status bit — "viewport is non-rectangularly
 * clipped" (Autodesk DXF reference AcDbViewport / ezdxf `acdb_viewport`
 * `VSF_NON_RECTANGULAR_CLIPPING`). Exported so the N7 VPCLIP-BOUNDARY-EXPORT
 * synthesis (`viewportClipObjectsSynthesize.ts`) can re-OR it back onto a
 * viewport's emitted `statusBitFlags` after this module's own per-entity mask
 * cleared it (an app-authored clip has no 340 handle / `clipBoundaryKind` at
 * the time `toDxfEntity` runs — the synthesis mints one LATER, in the
 * document-level export orchestrator).
 */
export const NONRECT_CLIP_BIT = 0x10000;

/**
 * Return `bits` with the group-90 non-rect-clip bit cleared UNLESS the viewport
 * emits a real clip boundary to pair with it — either a native 340 clip-boundary
 * handle OR a preserved imported SPATIAL_FILTER clip carrier (`clipBoundaryKind`).
 * A literal `"0"` is the documented no-clip sentinel, not a boundary reference,
 * so it does NOT count.
 */
export function maskOrphanNonRectClipBit(
  bits: number,
  vp: Pick<
    ViewportEntity,
    "clippingBoundaryHandle" | "extraGroups" | "clipBoundaryKind"
  >,
): number {
  const emitsRealClipHandle =
    (vp.clippingBoundaryHandle !== undefined &&
      vp.clippingBoundaryHandle !== "0") ||
    (vp.extraGroups?.some((g) => g.code === 340 && g.value !== "0") ?? false);
  // An imported SPATIAL_FILTER (XCLIP/VPCLIP) clip persists its boundary via the
  // ext-dict ACAD_FILTER → SPATIAL_FILTER chain (exported verbatim through the
  // OBJECTS passthrough), NOT a native 340 handle. `clipBoundaryKind` is set ONLY
  // for such a resolved carrier and cleared when the clip is removed/superseded,
  // so its presence means a valid source clip boundary is still emitted — the
  // source non-rect bit must be preserved, not masked as orphaned (@codex #496 P2).
  const emitsSpatialFilterClip = vp.clipBoundaryKind !== undefined;
  return emitsRealClipHandle || emitsSpatialFilterClip
    ? bits
    : bits & ~NONRECT_CLIP_BIT;
}
